/**
 * Shared sidebar navigation links for top-level app sections.
 */

export function buildHorizonSubItems({ embeddingCount, reviewCount } = {}) {
  return [
    { id: 'main', label: 'Data Ingestion', icon: 'fas fa-upload', to: '/horizon/main' },
    { id: 'data-embeddings', label: 'Data Embeddings', icon: 'fas fa-vector-square', to: '/horizon/data-embeddings', badge: embeddingCount || undefined },
    { id: 'data-review', label: 'Data Review', icon: 'fas fa-check-circle', to: '/horizon/data-review', badge: reviewCount || undefined },
    { id: 'data-management', label: 'Data Management', icon: 'fas fa-database', to: '/horizon/data-management' },
  ];
}

export function buildSettingsSubItems(user, { schedulerErrorCount } = {}) {
  const items = [
    { id: 'ingestion', label: 'Ingestion', icon: 'fas fa-cloud-upload-alt', to: '/settings/ingestion' },
    {
      id: 'scheduler',
      label: 'Scheduler',
      icon: 'fas fa-clock',
      to: '/settings/scheduler',
      badge: schedulerErrorCount || undefined,
      badgeTone: schedulerErrorCount ? 'danger' : undefined,
    },
    { id: 'schedules', label: 'Schedules (deprecated)', icon: 'fas fa-calendar-alt', to: '/settings/schedules' },
    { id: 'prompts', label: 'Prompts', icon: 'fas fa-cogs', to: '/settings/prompts' },
    { id: 'tools', label: 'Tools', icon: 'fas fa-tools', to: '/settings/tools' },
    { id: 'views', label: 'Views', icon: 'fas fa-filter', to: '/settings/views' },
    { id: 'yuml', label: 'YUML', icon: 'fas fa-file-code', to: '/settings/yuml' },
    { id: 'system', label: 'System', icon: 'fas fa-cog', to: '/settings/system' },
    { id: 'user', label: 'User', icon: 'fas fa-user', to: '/settings/user' },
  ];
  if (user?.accountType === 'admin' || user?.accountType === 'superadmin') {
    items.push({ id: 'admin', label: 'Admin', icon: 'fas fa-user-shield', to: '/settings/admin' });
  }
  if (user?.accountType === 'superadmin') {
    items.push({ id: 'superadmin', label: 'Superadmin', icon: 'fas fa-shield-alt', to: '/settings/superadmin' });
  }
  return items;
}

export function buildHelpSubItems(user) {
  const items = [
    { id: 'docs', label: 'Documentation', icon: 'fas fa-book', to: '/help/docs' },
    { id: 'chat', label: 'Ask Zagger', icon: 'fas fa-comments', to: '/help/chat' },
    { id: 'system-logs', label: 'System Logs', icon: 'fas fa-clipboard-list', to: '/help/system-logs' },
  ];
  if (user?.accountType === 'admin' || user?.accountType === 'superadmin') {
    items.push({ id: 'system-tests', label: 'System Test', icon: 'fas fa-vials', to: '/help/system-tests' });
  }
  return items;
}

export function buildHeadlessSubItems() {
  return [
    { id: 'review', label: 'Review', icon: 'fas fa-list-check', to: '/headless/review' },
    { id: 'integrations', label: 'Integrations', icon: 'fas fa-key', to: '/headless/integrations' },
  ];
}

function isAdminUser(user) {
  return user?.accountType === 'admin' || user?.accountType === 'superadmin';
}

export function buildMainNavLinks({
  user,
  horizonBadge,
  embeddingCount,
  reviewCount,
  schedulerErrorCount,
  settingsActiveId,
  helpActiveId,
  onSettingsClick,
  onHelpClick,
} = {}) {
  const settingsSubItems = buildSettingsSubItems(user, { schedulerErrorCount });
  const helpSubItems = buildHelpSubItems(user);

  const links = [
    { id: 'projects', label: 'Projects', icon: 'fas fa-folder-open', to: '/projects' },
    { id: 'research', label: 'Research', icon: 'fas fa-microscope', to: '/research' },
    {
      id: 'horizon',
      label: 'Horizon',
      icon: 'fas fa-layer-group',
      to: '/horizon',
      sectionBasePath: '/horizon',
      badge: horizonBadge || undefined,
      subItems: buildHorizonSubItems({ embeddingCount, reviewCount }),
    },
  ];

  if (isAdminUser(user)) {
    links.push({
      id: 'headless',
      label: 'Headless',
      icon: 'fas fa-plug',
      to: '/headless',
      sectionBasePath: '/headless',
      subItems: buildHeadlessSubItems(),
    });
  }

  links.push(
    {
      id: 'settings',
      label: 'Settings',
      icon: 'fas fa-gear',
      to: '/settings',
      sectionBasePath: '/settings',
      subItems: settingsSubItems,
    },
    {
      id: 'help',
      label: 'Help',
      icon: 'fas fa-circle-question',
      to: '/help',
      sectionBasePath: '/help',
      subItems: helpSubItems,
    },
  );

  return links.map((item) => {
    if (item.id === 'settings' && settingsActiveId && onSettingsClick) {
      return { ...item, activeItemHook: settingsActiveId, onItemClick: onSettingsClick };
    }
    if (item.id === 'help' && helpActiveId && onHelpClick) {
      return { ...item, activeItemHook: helpActiveId, onItemClick: onHelpClick };
    }
    return item;
  });
}

export default buildMainNavLinks;
